import { useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineClose, AiOutlineMenu, logo, owlFeature } from "../assets/images/image";
import { AnimatePresence, motion } from "framer-motion";
import useLenis from "../hooks/useLenis";
import useActiveSection from "../hooks/useActiveSection";
import { ScrollProvider } from "../hooks/ScrollContext";
import useGsapReveal from "../animations/useGsapReveal";
import useParallax from "../animations/useParallax";
import HomeSection from "../sections/HomeSection";
import FeaturesSection from "../sections/FeaturesSection";
import AISection from "../sections/AISection";
import HowItWorksSection from "../sections/HowItWorksSection";
import SubscriptionSection from "../sections/SubscriptionSection";
import DownloadSection from "../sections/DownloadSection";
import ContactSection from "../sections/ContactSection";

const sections = [
    { id: "home", label: "Home", Component: HomeSection },
    { id: "features", label: "Features", Component: FeaturesSection },
    { id: "ai-companion", label: "Ai-Companion", Component: AISection },
    { id: "how-it-works", label: "How it works", Component: HowItWorksSection },
    // { id: "subscription", label: "Subscription", Component: SubscriptionSection },
    { id: "download", label: "Download", Component: DownloadSection },
    { id: "contact", label: "Contact", Component: ContactSection },
];

const SinglePageLayout = () => {
    const [isOpen, setIsOpen] = useState(false);
    const layoutRef = useRef<HTMLDivElement>(null);
    const navRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    const { scrollTo } = useLenis();
    const sectionIds = useMemo(() => sections.map((section) => section.id), []);
    const activeSection = useActiveSection(sectionIds);
    const [activeNavId, setActiveNavId] = useState(sectionIds[0] ?? "");
    const activeIndex = Math.max(
        sectionIds.indexOf(activeNavId),
        0
    );
    const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });

    useGsapReveal(layoutRef);
    useParallax(layoutRef);

    const handleChoose = (id: string) => {
        const headerOffset = headerRef.current?.offsetHeight ?? 0;
        const offset = -(headerOffset + 12);
        setActiveNavId(id);
        scrollTo(`#${id}`, offset);
        setIsOpen(false);
    };

    useEffect(() => {
        if (activeSection) {
            setActiveNavId(activeSection);
        }
    }, [activeSection]);

    useEffect(() => {
        const container = navRef.current;
        if (!container || activeIndex < 0) return;

        const tab = container.children[activeIndex] as HTMLElement | undefined;
        if (!tab) return;

        const { offsetLeft, offsetWidth } = tab;
        setIndicatorStyle({ width: offsetWidth, left: offsetLeft + offsetWidth / 2 - 8 });
    }, [activeIndex]);

    useEffect(() => {
        const handleResize = () => {
            const container = navRef.current;
            if (!container || activeIndex < 0) return;

            const tab = container.children[activeIndex] as HTMLElement | undefined;
            if (!tab) return;

            const { offsetLeft, offsetWidth } = tab;
            setIndicatorStyle({ width: offsetWidth, left: offsetLeft + offsetWidth / 2 - 8 });
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [activeIndex]);

    useEffect(() => {
        if (!isOpen) {
            document.body.style.overflow = "";
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

    return (
        <ScrollProvider scrollToSection={handleChoose}>
            <div ref={layoutRef} className="relative w-full bg-white">
                <div className="fixed inset-0 z-0 pointer-events-none hidden md:block">
                    <img
                        src={owlFeature}
                        alt="Owl background"
                        className="absolute bottom-0 left-1/2 -translate-x-1/2"
                    />
                </div>
                <header ref={headerRef} className="sticky top-0 z-40 bg-white/90 backdrop-blur-md">
                    <div className="flex items-center min-h-[72px] md:min-h-0 pt-[10px] pb-[10px] mt-2 md:mt-[16px] mb-2 md:justify-between relative justify-center max-w-[1200px] mx-auto">
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="absolute left-4 top-1/2 -translate-y-1/2 md:hidden z-50 text-[#484848] focus:outline-none"
                            aria-label="Open menu"
                        >
                            <img src={AiOutlineMenu} alt="Menu" />
                        </button>
                        <img src={logo} alt="Logo" className="w-[112px] h-auto md:static absolute md:mt-0" />

                        <nav
                            ref={navRef}
                            className="relative px-4 py-2 flex items-center gap-[64px] md:block hidden md:flex"
                            aria-label="Primary navigation"
                        >
                            {sections.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => handleChoose(item.id)}
                                    className={`select-none relative text-[17px] font-inter text-[#484848] cursor-pointer transition-all duration-300 ${item.id === activeNavId ? "font-bold" : "font-normal"
                                        }`}
                                    aria-current={item.id === activeNavId ? "page" : undefined}
                                >
                                    {item.label}
                                </button>
                            ))}

                            <div
                                className="absolute bottom-0 bg-[#FFD600] transition-all duration-500 ease-in-out rounded-lg w-4 h-2"
                                style={{ left: `${indicatorStyle.left}px` }}
                            />
                        </nav>

                        <AnimatePresence>
                            {isOpen && (
                                <motion.div
                                    initial={{ x: "-100%" }}
                                    animate={{ x: 0 }}
                                    exit={{ x: "-100%" }}
                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                    className="fixed inset-0 w-screen h-screen bg-white z-50 overflow-y-auto px-6 pt-16 pb-10"
                                >
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="fixed top-4 right-4 rounded-full flex items-center justify-center bg-[#FFD600] w-[40px] h-[40px] focus:outline-none"
                                        aria-label="Close menu"
                                    >
                                        <img src={AiOutlineClose} alt="Close" className="w-[24px] h-[24px]" />
                                    </button>

                                    <div className="flex justify-center mb-10">
                                        <img src={logo} alt="Logo" className="w-[112px]" />
                                    </div>

                                    <ul className="text-left space-y-3">
                                        {sections.map((item) => (
                                            <li
                                                key={item.id}
                                                onClick={() => handleChoose(item.id)}
                                                className={`w-full cursor-pointer transition-all duration-300 font-inter text-opacity-70 ${item.id === activeNavId
                                                    ? "bg-[#FFD600] text-black px-5 py-3 rounded-[16px] font-bold text-[17px] leading-[100%] tracking-[0%]"
                                                    : "text-black px-5 py-3"
                                                    }`}
                                            >
                                                {item.label}
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </header>

                <main className="relative z-10">
                    {sections.map(({ id, Component }) => (
                        <Component key={id} />
                    ))}
                </main>
                <footer className="w-full border-t border-gray-200 relative z-10">
                    <div className="md:container px-6 py-6 flex flex-col md:flex-row items-center md:items-center justify-between text-[14px] md:text-[17px] font-inter text-[#212224] gap-2 md:gap-0 text-center">
                        <div className="hidden md:block" />
                        <div className="flex items-center gap-x-2">
                            <p className="font-normal">Copyright © 2025</p>
                            <p className="font-bold">VIYUME</p>
                        </div>
                        <div>Copyright | Privacy Policy | Terms of Service</div>
                    </div>
                </footer>
            </div>
        </ScrollProvider>
    );
};

export default SinglePageLayout;
